import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    defaultInputPadding,
    TextInput,
    TextInputProps,
} from 'components/forms/TextInput';
import { forwardRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { PickForStyled } from 'utils/types';

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

type SearchBoxProps = PickForStyled<
    SearchInputProps,
    'background' | 'isRounded' | 'inputSize' | 'showSearchClearButton'
>;

const SearchBox = styled(TextInput)<SearchBoxProps>`
    width: 100%;
    padding-left: ${(props) => props.padding ?? defaultInputPadding};
    ${(props) =>
        props.$background === 'transparent'
            ? 'background-color: transparent'
            : css`
                  background-color: ${tokens.background.default};
              `};

    /* account for the search icon, so text does not overlap */
    padding-right: ${(props) => (props.$inputSize === 'large' ? '3.5rem' : '3rem')};

    ${({ $showSearchClearButton }) =>
        !$showSearchClearButton &&
        css`
            &::-webkit-search-cancel-button {
                /* Remove default */
                appearance: none;

                /* hide */
                display: none;
            }
        `}
    ${(props) => (props.$isRounded ? 'border-radius: 50px;' : '')}

    &:disabled {
        color: ${tokens.text.disabled};
        cursor: not-allowed;
    }
`;

const SearchIcon = styled.div<{ $inputSize: 'medium' | 'large' }>`
    display: inline-block;
    position: absolute;
    right: ${(props) => (props.$inputSize === 'large' ? '1.5rem' : '1rem')};
    color: ${tokens.text.primary};
`;

export type SearchInputProps = Omit<TextInputProps, 'type' | 'padding'> & {
    /**
     * Whether or not to show the "X" clear button that clears the text in the field.
     */
    showSearchClearButton?: boolean;
    /**
     * Size of the search input. Defaults to `medium`.
     */
    inputSize?: 'medium' | 'large';
    /**
     * If true, search box will have rounded edges
     */
    isRounded?: boolean;
    /**
     * The background to apply to the search input. Defaults to `backgroundColor`.
     */
    background?: 'transparent' | 'backgroundColor';
};

/**
 * The `search`-type specific version of TextInput with appropriate styles added.
 */
function SearchInput(
    {
        showSearchClearButton,
        inputSize,
        isRounded = false,
        background = 'backgroundColor',
        ...rest
    }: SearchInputProps,
    ref: Ref<HTMLInputElement>
) {
    return (
        <Wrapper>
            <SearchBox
                ref={ref}
                type="search"
                $showSearchClearButton={showSearchClearButton ?? false}
                $inputSize={inputSize ?? 'medium'}
                $isRounded={isRounded}
                $background={background}
                padding={inputSize === 'large' ? '1.5rem' : '1rem'}
                data-cy="search-input"
                {...rest}
            />
            <SearchIcon $inputSize={inputSize ?? 'medium'}>
                <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
        </Wrapper>
    );
}

const forwardRefSearchInput = forwardRef(SearchInput);
export { forwardRefSearchInput as SearchInput };
